import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Zustand store for project/environment context.
 *
 * Replaces window.currentProjectId / window.currentSEnv / window.projects / window.sEnv.
 *
 * Written once in UserPool.js after ensureProjectEnvReady() / getProjects() resolves.
 * window.* globals are kept in sync (write-through) so existing consumers continue to work
 * during incremental migration.
 *
 * Usage in components:
 *   import useProjectStore from '../../store/projectStore';
 *   const currentProjectId = useProjectStore(s => s.currentProjectId);
 *
 * Usage outside React (services, plain functions):
 *   import { getProjectStore } from '../../store/projectStore';
 *   const { currentProjectId } = getProjectStore();
 */

const useProjectStore = create(devtools((set) => ({
    currentProjectId: localStorage.lastProjectId || null,
    currentSEnv:      localStorage.lastSEnvId    || null,
    projects:      null,
    sEnv:             null,
    projectsReady:    false,

    /** Called once after projects + environments are fetched. */
    setProjectContext: ({ currentProjectId, currentSEnv, projects, sEnv }) =>
        set({
            currentProjectId: currentProjectId ?? null,
            currentSEnv:      currentSEnv      ?? null,
            projects:      projects      ?? null,
            sEnv:             sEnv             ?? null,
            projectsReady:    !!(currentProjectId && currentSEnv),
        }),

    setCurrentProject: (projectId) =>
        set({ currentProjectId: projectId }),

    setCurrentSEnv: (sEnvId) =>
        set({ currentSEnv: sEnvId }),

    setProjects: (projects) =>
        set({ projects }),
}), { name: 'projectStore' }));

/** Access store state outside React (services, Apollo link, plain functions). */
export const getProjectStore = () => useProjectStore.getState();

// Keep window.currentProjectId in sync so cross-origin iframes (e.g. 3d/src/commons.js)
// can read it via window.parent.currentProjectId.
useProjectStore.subscribe((state) => {
    if (typeof window !== 'undefined') {
        window.currentProjectId = state.currentProjectId;
    }
});

export default useProjectStore;
